import type { IBuild } from '@/entities/build.entity';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupInput, InputGroupTextarea } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type { SenderExternalChannelMode } from '@/api/sender-presets';
import {
	SENDER_APK_DELIVERY_METHOD_OPTIONS,
	shouldRequireBuildForCreateRun,
	shouldRequireCaptionForCreateRun,
	type SenderApkDeliveryMethod,
} from '@/lib/sender-apk-delivery';
import {
	SENDER_AUDIENCE_FILTER_OPTIONS,
	type SenderAudienceFilter,
} from '@/lib/sender-audience-filter';
import SenderBuildPicker from '@/pages/sender/components/sender-build-picker';

const CAPTION_PLACEHOLDER = 'Текст сообщения к APK';

const EXTERNAL_CHANNEL_MODE_OPTIONS: Array<{
	value: SenderExternalChannelMode;
	label: string;
	hint: string;
}> = [
	{
		value: 'latest_message',
		label: 'Последнее сообщение',
		hint: 'Будет скопировано последнее сообщение канала (не ссылка на пост).',
	},
	{
		value: 'post_link',
		label: 'Ссылка на пост',
		hint: 'Укажите URL поста в канале (t.me/channel/123 или t.me/c/id/msg).',
	},
];

export interface SenderDeliveryStepProps {
	deliveryProfileReady: boolean;
	deliveryProfileError: boolean;
	onRetryDeliveryProfile?: () => void;
	apkDeliveryMethod: SenderApkDeliveryMethod;
	onApkDeliveryMethodChange: (method: SenderApkDeliveryMethod) => void;
	caption: string;
	onCaptionChange: (v: string) => void;
	externalChannelUrl: string;
	onExternalChannelUrlChange: (v: string) => void;
	externalChannelMode?: SenderExternalChannelMode;
	onExternalChannelModeChange?: (mode: SenderExternalChannelMode) => void;
	audienceFilter: SenderAudienceFilter;
	onAudienceFilterChange: (v: SenderAudienceFilter) => void;
	builds: IBuild[];
	buildId: string;
	onBuildIdChange: (id: string) => void;
}

export default function SenderDeliveryStep({
	deliveryProfileReady,
	deliveryProfileError,
	onRetryDeliveryProfile,
	apkDeliveryMethod,
	onApkDeliveryMethodChange,
	caption,
	onCaptionChange,
	externalChannelUrl,
	onExternalChannelUrlChange,
	externalChannelMode = 'latest_message',
	onExternalChannelModeChange,
	audienceFilter,
	onAudienceFilterChange,
	builds,
	buildId,
	onBuildIdChange,
}: SenderDeliveryStepProps) {
	const requiresBuild = shouldRequireBuildForCreateRun(apkDeliveryMethod);
	const requiresCaption = shouldRequireCaptionForCreateRun(apkDeliveryMethod);
	const externalModeHint =
		EXTERNAL_CHANNEL_MODE_OPTIONS.find(option => option.value === externalChannelMode)?.hint ??
		EXTERNAL_CHANNEL_MODE_OPTIONS[0].hint;
	const captionPreview = caption.trim();

	return (
		<div className='flex flex-col gap-4'>
			{deliveryProfileError ? (
				<div className='flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3'>
					<p className='text-sm text-destructive'>Не удалось загрузить настройки рассылки</p>
					<Button type='button' variant='outline' size='sm' onClick={() => onRetryDeliveryProfile?.()}>
						Повторить
					</Button>
				</div>
			) : null}

			<div className='flex flex-col gap-1.5'>
				<Label>Метод APK</Label>
				<Select
					value={apkDeliveryMethod}
					disabled={!deliveryProfileReady}
					onValueChange={value => onApkDeliveryMethodChange(value as SenderApkDeliveryMethod)}
				>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{SENDER_APK_DELIVERY_METHOD_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{requiresCaption ? (
				<div className='flex flex-col gap-1.5'>
					<Label htmlFor='launch-run-caption'>Текст сообщения</Label>
					<InputGroup>
						<InputGroupTextarea
							id='launch-run-caption'
							placeholder={CAPTION_PLACEHOLDER}
							value={caption}
							onChange={e => onCaptionChange(e.target.value)}
							maxLength={1024}
							rows={3}
						/>
					</InputGroup>
					{captionPreview ? (
						<div className='rounded-md border border-border bg-muted/40 px-3 py-2'>
							<p className='text-xs font-medium text-muted-foreground mb-1'>Превью подписи</p>
							<p className='text-sm whitespace-pre-wrap break-words'>{captionPreview}</p>
						</div>
					) : null}
				</div>
			) : (
				<div className='flex flex-col gap-3'>
					{onExternalChannelModeChange ? (
						<div className='flex flex-col gap-1.5'>
							<Label>Источник в канале</Label>
							<Select
								value={externalChannelMode}
								disabled={!deliveryProfileReady}
								onValueChange={value =>
									onExternalChannelModeChange(value as SenderExternalChannelMode)
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{EXTERNAL_CHANNEL_MODE_OPTIONS.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : null}
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='launch-external-channel-url'>
							{externalChannelMode === 'post_link' ? 'Ссылка на пост' : 'Ссылка на канал'}
						</Label>
						<InputGroup>
							<InputGroupInput
								id='launch-external-channel-url'
								placeholder={
									externalChannelMode === 'post_link'
										? 'https://t.me/example/123 или t.me/c/1234567890/42'
										: 'https://t.me/example или t.me/c/1234567890'
								}
								value={externalChannelUrl}
								onChange={e => onExternalChannelUrlChange(e.target.value)}
								maxLength={512}
							/>
						</InputGroup>
						<p className='text-xs text-muted-foreground'>{externalModeHint}</p>
					</div>
				</div>
			)}

			<div className='flex flex-col gap-1.5'>
				<Label>Аудитория</Label>
				<Select
					value={audienceFilter}
					disabled={!deliveryProfileReady}
					onValueChange={value => onAudienceFilterChange(value as SenderAudienceFilter)}
				>
					<SelectTrigger>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{SENDER_AUDIENCE_FILTER_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{requiresBuild ? (
				<SenderBuildPicker
					builds={builds}
					buildId={buildId}
					onBuildIdChange={onBuildIdChange}
					disabled={!deliveryProfileReady}
					label='Билд APK'
					dialogTitle='Билд APK'
				/>
			) : null}
		</div>
	);
}
